import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectContext = createContext(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [brandKit, setBrandKit] = useState(null);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState({ openai: false, fal: false });

  // Check API health
  const checkApiHealth = async () => {
    try {
      const response = await axios.get(`${API}/health`);
      setApiStatus({
        openai: response.data.openai_configured,
        fal: response.data.fal_configured
      });
    } catch (error) {
      console.error('Health check failed:', error);
    }
  };

  // Fetch all projects
  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  // Create a new project
  const createProject = async (projectData) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/projects`, projectData);
      setProjects(prev => [...prev, response.data]);
      return response.data;
    } catch (error) {
      console.error('Failed to create project:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Select a project
  const selectProject = async (projectId) => {
    try {
      setLoading(true);
      const projectRes = await axios.get(`${API}/projects/${projectId}`);
      setCurrentProject(projectRes.data);
      
      // Fetch brand kit
      try {
        const brandKitRes = await axios.get(`${API}/brand-kits/${projectId}`);
        setBrandKit(brandKitRes.data);
      } catch {
        setBrandKit(null);
      }
      
      // Fetch assets
      try {
        const assetsRes = await axios.get(`${API}/assets/${projectId}`);
        setAssets(assetsRes.data);
      } catch {
        setAssets([]);
      }
    } catch (error) {
      console.error('Failed to select project:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Partial update to a project (e.g. client contact info)
  const updateProject = async (projectId, updates) => {
    try {
      const response = await axios.patch(`${API}/projects/${projectId}`, updates);
      setProjects(prev => prev.map(p => (p.id === projectId ? response.data : p)));
      if (currentProject?.id === projectId) {
        setCurrentProject(response.data);
      }
      return response.data;
    } catch (error) {
      console.error('Failed to update project:', error);
      throw error;
    }
  };

  // Delete a project
  const deleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      setProjects(prev => prev.filter(p => p.id !== projectId));
      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        setBrandKit(null);
        setAssets([]);
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      throw error;
    }
  };

  // Create or update brand kit
  const saveBrandKit = async (brandKitData) => {
    try {
      setLoading(true);
      if (brandKit) {
        const response = await axios.put(`${API}/brand-kits/${currentProject.id}`, brandKitData);
        setBrandKit(response.data);
      } else {
        const response = await axios.post(`${API}/brand-kits`, {
          ...brandKitData,
          project_id: currentProject.id
        });
        setBrandKit(response.data);
      }
    } catch (error) {
      console.error('Failed to save brand kit:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Generate image
  const generateImage = async (prompt, assetType = 'general', width = 1024, height = 1024) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/generate-image`, {
        project_id: currentProject.id,
        prompt,
        asset_type: assetType,
        width,
        height
      });
      
      if (response.data.success) {
        // Refresh assets
        const assetsRes = await axios.get(`${API}/assets/${currentProject.id}`);
        setAssets(assetsRes.data);
        return response.data.data;
      }
    } catch (error) {
      console.error('Failed to generate image:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Delete asset
  const deleteAsset = async (assetId) => {
    try {
      await axios.delete(`${API}/assets/${assetId}`);
      setAssets(prev => prev.filter(a => a.id !== assetId));
    } catch (error) {
      console.error('Failed to delete asset:', error);
      throw error;
    }
  };

  // Refresh assets
  const refreshAssets = async () => {
    if (!currentProject) return;
    try {
      const assetsRes = await axios.get(`${API}/assets/${currentProject.id}`);
      setAssets(assetsRes.data);
    } catch (error) {
      console.error('Failed to refresh assets:', error);
    }
  };

  useEffect(() => {
    fetchProjects();
    checkApiHealth();
  }, []);

  const value = {
    projects,
    currentProject,
    brandKit,
    assets,
    loading,
    apiStatus,
    fetchProjects,
    createProject,
    selectProject,
    updateProject,
    deleteProject,
    saveBrandKit,
    generateImage,
    deleteAsset,
    refreshAssets,
    setBrandKit
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
